import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer,
} from "react";
import productData from "../Datas/productData";

const CART_STORAGE_KEY = "cartItems";
const WISHLIST_STORAGE_KEY = "wishlistItems";

const productPrices = new Map(
  productData.map((product) => [product.id, Number(product.price)])
);

const normalizeProductId = (value) => {
  const source =
    value && typeof value === "object" && "id" in value ? value.id : value;
  const numericId = Number(source);

  return Number.isFinite(numericId) && numericId > 0 ? numericId : null;
};

const normalizeOption = (value) =>
  value === undefined || value === null ? "" : String(value);

const normalizeQuantity = (value) => {
  const numericQuantity = Number(value);

  return Number.isFinite(numericQuantity)
    ? Math.max(0, Math.floor(numericQuantity))
    : 0;
};

const sanitizeCartItems = (value) => {
  if (!Array.isArray(value)) return [];

  return value.reduce((items, item) => {
    if (!item || typeof item !== "object") return items;

    const id = normalizeProductId(item.id);
    const quantity = normalizeQuantity(item.quantity);
    if (!id || !quantity) return items;

    const size = normalizeOption(item.size);
    const color = normalizeOption(item.color);
    const existingItem = items.find(
      (candidate) =>
        candidate.id === id &&
        candidate.size === size &&
        candidate.color === color
    );

    if (existingItem) {
      existingItem.quantity += quantity;
    } else {
      items.push({ id, size, color, quantity });
    }

    return items;
  }, []);
};

const sanitizeWishlistItems = (value) => {
  if (!Array.isArray(value)) return [];

  return [...new Set(value.map(normalizeProductId).filter(Boolean))];
};

const getStorage = () => {
  try {
    return typeof window !== "undefined" ? window.localStorage : null;
  } catch {
    return null;
  }
};

const readStorage = (key, sanitizer) => {
  try {
    const storage = getStorage();
    const savedValue = storage?.getItem(key);
    return savedValue ? sanitizer(JSON.parse(savedValue)) : [];
  } catch {
    return [];
  }
};

const writeStorage = (key, value) => {
  try {
    getStorage()?.setItem(key, JSON.stringify(value));
  } catch {
    // The store remains fully usable when storage is unavailable or full.
  }
};

const createInitialState = () => ({
  cartItems: readStorage(CART_STORAGE_KEY, sanitizeCartItems),
  wishlistItems: readStorage(
    WISHLIST_STORAGE_KEY,
    sanitizeWishlistItems
  ),
});

const reducer = (state, action) => {
  switch (action.type) {
    case "ADD_TO_CART": {
      const { id, quantity, size, color } = action.payload;
      const existingItem = state.cartItems.find(
        (item) =>
          item.id === id && item.size === size && item.color === color
      );

      if (existingItem) {
        return {
          ...state,
          cartItems: state.cartItems.map((item) =>
            item.id === id && item.size === size && item.color === color
              ? { ...item, quantity: item.quantity + quantity }
              : item
          ),
        };
      }

      return {
        ...state,
        cartItems: [...state.cartItems, { id, quantity, size, color }],
      };
    }

    case "REMOVE_FROM_CART": {
      const { id, size, color, matchVariant } = action.payload;

      return {
        ...state,
        cartItems: state.cartItems.filter((item) => {
          if (item.id !== id) return true;
          if (!matchVariant) return false;
          return item.size !== size || item.color !== color;
        }),
      };
    }

    case "UPDATE_QUANTITY": {
      const { id, size, color, quantity } = action.payload;

      if (quantity === 0) {
        return {
          ...state,
          cartItems: state.cartItems.filter(
            (item) =>
              !(
                item.id === id &&
                item.size === size &&
                item.color === color
              )
          ),
        };
      }

      return {
        ...state,
        cartItems: state.cartItems.map((item) =>
          item.id === id && item.size === size && item.color === color
            ? { ...item, quantity }
            : item
        ),
      };
    }

    case "CLEAR_CART":
      return { ...state, cartItems: [] };

    case "ADD_TO_WISHLIST":
      return state.wishlistItems.includes(action.payload)
        ? state
        : {
            ...state,
            wishlistItems: [...state.wishlistItems, action.payload],
          };

    case "REMOVE_FROM_WISHLIST":
      return {
        ...state,
        wishlistItems: state.wishlistItems.filter(
          (productId) => productId !== action.payload
        ),
      };

    case "TOGGLE_WISHLIST":
      return state.wishlistItems.includes(action.payload)
        ? {
            ...state,
            wishlistItems: state.wishlistItems.filter(
              (productId) => productId !== action.payload
            ),
          }
        : {
            ...state,
            wishlistItems: [...state.wishlistItems, action.payload],
          };

    case "CLEAR_WISHLIST":
      return { ...state, wishlistItems: [] };

    default:
      return state;
  }
};

export const CartContext = createContext(undefined);

export const CartProvider = ({ children }) => {
  const [state, dispatch] = useReducer(reducer, undefined, createInitialState);

  useEffect(() => {
    writeStorage(CART_STORAGE_KEY, state.cartItems);
  }, [state.cartItems]);

  useEffect(() => {
    writeStorage(WISHLIST_STORAGE_KEY, state.wishlistItems);
  }, [state.wishlistItems]);

  const addToCart = useCallback(
    (productId, quantity = 1, size = "", color = "") => {
      const id = normalizeProductId(productId);
      const normalizedQuantity = normalizeQuantity(quantity);
      if (!id || !normalizedQuantity) return;

      dispatch({
        type: "ADD_TO_CART",
        payload: {
          id,
          quantity: normalizedQuantity,
          size: normalizeOption(size),
          color: normalizeOption(color),
        },
      });
    },
    []
  );

  const removeFromCart = useCallback((productId, size, color) => {
    const id = normalizeProductId(productId);
    if (!id) return;

    dispatch({
      type: "REMOVE_FROM_CART",
      payload: {
        id,
        size: normalizeOption(size),
        color: normalizeOption(color),
        matchVariant: size !== undefined || color !== undefined,
      },
    });
  }, []);

  const updateQuantity = useCallback(
    (productId, size = "", color = "", quantity = 1) => {
      const id = normalizeProductId(productId);
      const normalizedQuantity = normalizeQuantity(quantity);
      if (!id) return;

      dispatch({
        type: "UPDATE_QUANTITY",
        payload: {
          id,
          size: normalizeOption(size),
          color: normalizeOption(color),
          quantity: normalizedQuantity,
        },
      });
    },
    []
  );

  const clearCart = useCallback(() => {
    dispatch({ type: "CLEAR_CART" });
  }, []);

  const addToWishlist = useCallback((productId) => {
    const id = normalizeProductId(productId);
    if (id) dispatch({ type: "ADD_TO_WISHLIST", payload: id });
  }, []);

  const removeFromWishlist = useCallback((productId) => {
    const id = normalizeProductId(productId);
    if (id) dispatch({ type: "REMOVE_FROM_WISHLIST", payload: id });
  }, []);

  const toggleWishlist = useCallback((productId) => {
    const id = normalizeProductId(productId);
    if (id) dispatch({ type: "TOGGLE_WISHLIST", payload: id });
  }, []);

  const clearWishlist = useCallback(() => {
    dispatch({ type: "CLEAR_WISHLIST" });
  }, []);

  const isWishlisted = useCallback(
    (productId) => {
      const id = normalizeProductId(productId);
      return id ? state.wishlistItems.includes(id) : false;
    },
    [state.wishlistItems]
  );

  const cartCount = useMemo(
    () =>
      state.cartItems.reduce((total, item) => total + item.quantity, 0),
    [state.cartItems]
  );

  const subtotal = useMemo(() => {
    const total = state.cartItems.reduce((sum, item) => {
      const price = productPrices.get(item.id);
      return Number.isFinite(price) ? sum + price * item.quantity : sum;
    }, 0);

    return Math.round((total + Number.EPSILON) * 100) / 100;
  }, [state.cartItems]);

  const value = useMemo(
    () => ({
      cartItems: state.cartItems,
      wishlistItems: state.wishlistItems,
      wishlist: state.wishlistItems,
      cartCount,
      wishlistCount: state.wishlistItems.length,
      subtotal,
      addToCart,
      removeFromCart,
      updateQuantity,
      clearCart,
      addToWishlist,
      addWishlist: addToWishlist,
      removeFromWishlist,
      removeWishlist: removeFromWishlist,
      toggleWishlist,
      clearWishlist,
      isWishlisted,
    }),
    [
      state.cartItems,
      state.wishlistItems,
      cartCount,
      subtotal,
      addToCart,
      removeFromCart,
      updateQuantity,
      clearCart,
      addToWishlist,
      removeFromWishlist,
      toggleWishlist,
      clearWishlist,
      isWishlisted,
    ]
  );

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};

export const useCart = () => {
  const context = useContext(CartContext);

  if (!context) {
    throw new Error("useCart must be used within a CartProvider");
  }

  return context;
};
